import React, { FC, ReactElement } from 'react'
import PlusIcon from '@/icons/plus-circle'
import MinusIcon from '@/icons/minus-circle'
import InscriptionCard from './transfer-card'
import formatNumber from '@/utils/format-number'
import Input from '@/components/input'
import { NETWORK_FUNGIBLE_PROTOCOL } from '@/utils/constants'
import useSatoshiPrice from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import ButtonGradientEpic from '@/components/button-gradient-epic'
import Button from '@/components/button'
import useInscribe from '@/utils/hooks/useInscribe'

const BRC20Transfer: FC<{
	ticker: string
	balance: any
	onComplete: () => void
}> = (props): ReactElement => {
	const { ticker, balance, onComplete } = props
	const [numTransfers, setTransfers] = React.useState(1)
	const [_feeRate, _setFeeRate] = React.useState(10.0)
	const [fee, _setFee] = React.useState(0)
	const [_loadingFee, _setLoadingFee] = React.useState(false)
	// useExchangeRate + useSatoshiPrice kept for subscription side-effects; values unused.
	useExchangeRate()
	useSatoshiPrice(fee)
	const { inscribeBulk } = useInscribe()

	const transfers = React.useMemo(() => new Array(numTransfers).fill(''), [numTransfers])
	const [amounts, setAmounts] = React.useState({})

	const files = React.useMemo(() => {
		const values = transfers.map((e, i) =>
			(amounts as any)[i] ? parseInt((amounts as any)[i], 10) : 0
		)

		return values.map((e) => {
			const type = balance.type || NETWORK_FUNGIBLE_PROTOCOL
			const payload = JSON.stringify({
				p: type,
				op: type === 'tap' ? 'token-transfer' : 'transfer',
				tick: ticker,
				amt: `${e}`
			})
			console.log({ payload })
			const file = new File([payload], `transfer-${e}-${ticker}.txt`, {
				type: 'text/plain'
			})

			return file
		})
	}, [transfers, amounts, balance.type, ticker])

	const handleSubmit = React.useCallback(() => {
		inscribeBulk(files)
		onComplete()
	}, [files, inscribeBulk, onComplete])

	const totalAmount = React.useMemo(
		() =>
			transfers
				.map((e, i) => ((amounts as any)[i] ? parseInt((amounts as any)[i], 10) : 0))
				.reduce((a, e) => a + e, 0),
		[amounts, transfers]
	)
	const currentAvailable = React.useMemo(
		() => parseFloat(balance.available_balance) - totalAmount,
		[totalAmount, balance]
	)

	const renderTransfer = React.useCallback(
		(e: any, i: any) => {
			return (
				<InscriptionCard
					key={i}
					ticker={ticker}
					description={`Available: ${formatNumber(`${Math.max(currentAvailable, 0)}`)}`}
					collection={balance.collection}
					onRemove={() => {}}
				>
					<Input
						placeholder="Amount to inscribe"
						className="border border-border rounded-full h-10"
						inputClassName="text-sm rounded-full"
						type="number"
						onChange={(evt) => {
							setAmounts((prev) => ({ ...prev, [i]: evt.target.value }))
						}}
						value={(amounts as any)[i] || ''}
					/>
				</InscriptionCard>
			)
		},
		[ticker, currentAvailable, balance.collection, amounts]
	)

	return (
		<div>
			<div className="flex justify-between">
				<p className="text-base font-bold">Inscribe Transfer ({numTransfers})</p>
				<div className="flex gap-4">
					<MinusIcon
						className="size-[20px] stroke-[#B692F6]"
						onClick={() => setTransfers(Math.max(1, numTransfers - 1))}
					/>
					<PlusIcon
						className="size-[20px] stroke-[#B692F6]"
						onClick={() => setTransfers(numTransfers + 1)}
					/>
				</div>
			</div>
			<div className="bg-card p-4 flex flex-col gap-4 rounded-xl border border-border mt-4">
				{transfers.map(renderTransfer)}
			</div>
			<div className="mt-6">
				<ButtonGradientEpic
					disabled={false}
					onClick={handleSubmit}
					className="h-[48px] rounded-full w-full"
				>
					Inscribe Now
				</ButtonGradientEpic>
				<Button
					className="text-muted-foreground rounded-full text-base font-semibold p-0 h-12"
					onClick={onComplete}
				>
					Close
				</Button>
			</div>
		</div>
	)
}

export default BRC20Transfer
